import React, { useEffect, useState } from 'react';
import { Dialog, DialogActions, Grid } from '@material-ui/core';
import { useTranslation } from '@michelin/central-provider';
import { Controller, useForm } from 'react-hook-form';
import {
  Button, Select, SelectOption, useDialogs,
} from '@michelin/acid-components';
import { getOptionLabel, getOptions } from '@michelin/select-options-provider';
import PreventTransitionDialog
  from '@michelin/acid-components/build/components/PreventTransitions/PreventTransitionDialog';
import { DealerAutocomplete } from 'components/Autocomplete';
import { ClosableDialogTitle } from 'components/ClosableDialog/ClosableDialogTitle';
import { ClosableDialogContent } from 'components/ClosableDialog/ClosableDialogContent';
import { scrollToDisplayFirstError } from 'Utils';
import usePermissionsSPP from '../../../hooks/usePermissionsSPP';
import { MechanicCertification } from '../../../Types';
import FileUpload from '../../FileUpload/FileUpload';

interface MechanicCertificationsDetailsProps {
  action: string;
  defaultValues: MechanicCertification;
  onSave: (data:MechanicCertification[]) => void;
  onClose: () => void;
  title?: string;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  fileOnly?: boolean;
}

function MechanicCertificationsDetail(props:MechanicCertificationsDetailsProps) {
  const { fileOnly } = props;
  const { t } = useTranslation();
  const { errorDialog } = useDialogs();
  const permissions = usePermissionsSPP();

  const {
    register, formState: { errors, isDirty, isSubmitting }, control, watch, handleSubmit,
  } = useForm<any>({ defaultValues: props.defaultValues });

  const [preventTransition, setPreventTransition] = useState<boolean>(false);
  const [uploading, setUploading] = useState<boolean>(false);
  const [jobs, setJobs] = useState<SelectOption[]>([]);
  const [states, setStates] = useState<SelectOption[]>([]);
  const [tiaCertifications, setTiaCertifications] = useState<SelectOption[]>([]);
  const [aseCertifications, setAseCertifications] = useState<SelectOption[]>([]);

  const typeName = watch('type');
  const jobName = watch('job');
  const stateName = watch('state');
  const tiaCertsName = watch('tia_certified');

  const jobsList = (props.action === 'add') ? jobName || [] : [jobName];
  const statesList = (props.action === 'add') ? stateName || [] : [stateName];
  const tiaCertsList = (props.action === 'add') ? tiaCertsName || [] : [tiaCertsName];

  const title = props.title ?? t('Technician Mechanic Certifications & Training');

  const save = async (data: any) => {
    // Check that JobName is part of the list for that typeName

    if (props.action === 'edit') props.onSave([data]);
    else {
      const response:MechanicCertification[] = [];

      data.job.forEach((job:string) => {
        let file = null;
        if (data.type === 'mechanical' && job === 'state_inspection') return;
        if (data.type === 'tire_certifications') return;
        if (data.type === 'mechanical' && job === 'basic_brake_service' && permissions.location?.customer_country?.toLowerCase() === 'us') file = data.files.mechanical.basic_brake_service;
        if (data.type === 'mechanical' && job === 'dot_annual_inspection') file = data.files.mechanical.dot_annual_inspection;
        response.push({
          id: data.id,
          type: data.type,
          job,
          file,
        });
      });

      if (data.state) {
        // We have mechanical state inspections
        data.state.forEach((state:string) => {
          response.push({
            id: data.id,
            type: data.type,
            job: 'state_inspection',
            state,
            file: data.files.mechanical.state_inspection[state],
          });
        });
      }

      if (data.ase_certified && data.job.includes('ase_certified')) {
        // We have tire certifications with ase certifications
        data.ase_certified.forEach((certification: string) => {
          response.push({
            id: data.id,
            type: data.type,
            job: 'ase_certified',
            certification,
          });
        });
      }

      if (data.tia_certified && data.job.includes('tia_certified')) {
        // We have tire certifications with tia certifications
        data.tia_certified.forEach((certification: string) => {
          response.push({
            id: data.id,
            type: data.type,
            job: 'tia_certified',
            certification,
            file: data.files.tire_certifications.tia_certified[certification],
          });
        });
      }

      props.onSave(response);
    }
  };

  const onError = (errors: any) => {
    errorDialog(
      t('Please check the marked fields.').split('\n'),
      t('Error saving certification information.'),
    );
    scrollToDisplayFirstError();
  };

  const types = (permissions.location?.customer_country?.toLowerCase() === 'ca')
    ? getOptions('spp_mechanical_certifications_services_ca')
    : getOptions('spp_mechanical_certifications_services_us');

  useEffect(() => {
    if (typeName === 'tire') setJobs(getOptions('spp_mechanical_certifications_jobs_tire'));
    if (typeName === 'tire_certifications') setJobs(getOptions('spp_mechanical_certifications_jobs_tire_certifications'));
    if (typeName === 'mechanical') {
      if (permissions.location?.customer_country?.toLowerCase() === 'ca') setJobs(getOptions('spp_mechanical_certifications_jobs_mechanical_ca'));
      else setJobs(getOptions('spp_mechanical_certifications_jobs_mechanical_us'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeName]);

  useEffect(() => {
    if (!jobName) return;
    if (typeName === 'mechanical' && jobsList.includes('state_inspection')) {
      setStates(getOptions('us_states'));
    } else {
      setStates([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeName, jobName]);

  useEffect(() => {
    if (typeName === 'tire_certifications' && jobsList.includes('ase_certified')) {
      setAseCertifications(getOptions('spp_mechanical_certifications_certifications_ase'));
    } else {
      setAseCertifications([]);
    }
    if (typeName === 'tire_certifications' && jobsList.includes('tia_certified')) {
      setTiaCertifications(getOptions('spp_mechanical_certifications_certifications_tia'));
    } else {
      setTiaCertifications([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeName, jobName]);

  return (
    <>
      <Dialog
        onClose={() => {
          if (isDirty || uploading || isSubmitting) setPreventTransition(true);
          else props.onClose();
        }}
        aria-labelledby="customized-dialog-title"
        open
        fullWidth
        maxWidth={props.maxWidth ?? 'sm'}
        scroll="paper"
      >
        <ClosableDialogTitle
          onClose={() => {
            if (isDirty || uploading || isSubmitting) setPreventTransition(true);
            else props.onClose();
          }}
        >
          {title}
        </ClosableDialogTitle>
        <ClosableDialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} style={{ marginTop: 7 }}>
              <Controller
                control={control}
                name="type"
                rules={{ required: true }}
                render={({ field }) => (
                  <Select
                    id="type"
                    label={t('Service/Certification Type')}
                    options={types}
                    error={errors.type}
                    readOnly={false}
                    required
                    fullWidth
                    disabled={fileOnly}
                    onChange={(e: any) => { field.onChange(e.target.value); }}
                    value={field.value}
                    defaultValue=""
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} style={{ marginTop: 7 }}>
              {props.action === 'add'
                ? (
                  <Controller
                    control={control}
                    name="job"
                    rules={{ required: true }}
                    render={({ field }) => (
                      <DealerAutocomplete
                        id="job"
                        variant="standard"
                        multiple
                        disableClearable
                        suggestions={jobs}
                        placeholder={t('Job Functions/Certifications')}
                        label={t('Job Functions/Certifications')}
                        values={field.value}
                        disabledOptions={[]}
                        readOnly={false}
                        disabled={!typeName}
                        error={!!errors.job}
                        disableCloseOnSelect
                        onChange={(newValue: string[]) => field.onChange(newValue)}
                      />
                    )}
                  />
                )
                : (
                  <Controller
                    control={control}
                    name="job"
                    rules={{ required: true }}
                    render={({ field }) => (
                      <Select
                        id="job"
                        label={t('Job Functions/Certifications')}
                        options={jobs}
                        error={errors.job}
                        readOnly={false}
                        disabled={!jobs || fileOnly}
                        required
                        fullWidth
                        onChange={(e: any) => { field.onChange(e.target.value); }}
                        value={field.value}
                        defaultValue=""
                      />
                    )}
                  />
                )}
            </Grid>

            {typeName === 'mechanical' && jobsList.includes('basic_brake_service') && permissions.location?.customer_country?.toLowerCase() === 'us'
              ? (
                <Grid item xs={12} style={{ marginTop: 7 }}>
                  <Controller
                    control={control}
                    shouldUnregister
                    /* eslint-disable-next-line react/jsx-props-no-spreading */
                    {...register(props.action === 'edit' ? 'file' : 'files.mechanical.basic_brake_service', { required: true })}
                    render={(item) => (
                      <FileUpload
                        error={!!errors?.files?.mechanical?.basic_brake_service}
                        data={item.field.value}
                        onChange={item.field.onChange}
                        onUploading={setUploading}
                        label={`${getOptionLabel('spp_mechanical_certifications_jobs_mechanical_us', 'basic_brake_service')} ${t('Certification Documents')}`}
                        required
                        readOnly={false}
                        mode="minimal"
                      />
                    )}
                  />
                </Grid>
              )
              : null }

            {typeName === 'mechanical' && jobsList.includes('dot_annual_inspection')
              ? (
                <Grid item xs={12} style={{ marginTop: 7 }}>
                  <Controller
                    control={control}
                    shouldUnregister
                    /* eslint-disable-next-line react/jsx-props-no-spreading */
                    {...register(props.action === 'edit' ? 'file' : 'files.mechanical.dot_annual_inspection', { required: true })}
                    render={(item) => (
                      <FileUpload
                        error={!!errors?.files?.mechanical?.dot_annual_inspection}
                        data={item.field.value}
                        onChange={item.field.onChange}
                        onUploading={setUploading}
                        label={`${getOptionLabel('spp_mechanical_certifications_jobs_mechanical_us', 'dot_annual_inspection')} ${t('Certification Documents')}`}
                        required
                        readOnly={false}
                        mode="minimal"
                      />
                    )}
                  />
                </Grid>
              )
              : null }

            {states.length ? (
              <Grid item xs={12} style={{ marginTop: 7 }}>
                {props.action === 'add'
                  ? (
                    <Controller
                      control={control}
                      name="state"
                      shouldUnregister
                      rules={{ required: true }}
                      render={({ field }) => (
                        <DealerAutocomplete
                          id="state"
                          variant="standard"
                          multiple
                          disableClearable
                          suggestions={states}
                          placeholder={t('State')}
                          label={t('State')}
                          values={field.value}
                          disabledOptions={[]}
                          readOnly={false}
                          disabled={!states}
                          error={!!errors.state}
                          disableCloseOnSelect
                          onChange={(newValue: string[]) => field.onChange(newValue)}
                        />
                      )}
                    />
                  ) : (
                    <Controller
                      control={control}
                      name="state"
                      shouldUnregister
                      rules={{ required: true }}
                      render={({ field }) => (
                        <Select
                          id="state"
                          label={t('State')}
                          options={states}
                          error={errors.state}
                          readOnly={false}
                          // disabled={!states || fileOnly}
                          required
                          fullWidth
                        /* eslint-disable-next-line react/jsx-props-no-spreading */
                          {...field}
                        />
                      )}
                    />
                  )}
              </Grid>
            ) : null}

            {typeName === 'mechanical' && jobsList.includes('state_inspection') && statesList.map((st:string) => (
              <Grid item xs={12} style={{ marginTop: 7 }}>
                <Controller
                  control={control}
                  shouldUnregister
                  /* eslint-disable-next-line react/jsx-props-no-spreading */
                  {...register(props.action === 'edit' ? 'file' : `files.mechanical.state_inspection.${st}`, { required: true })}
                  render={(item) => (
                    <FileUpload
                      error={!!errors?.files?.mechanical?.state_inspection[st]}
                      data={item.field.value}
                      onChange={item.field.onChange}
                      onUploading={setUploading}
                      label={`${getOptionLabel('spp_mechanical_certifications_jobs_mechanical_us', 'state_inspection')} - ${getOptionLabel('us_states', st)} ${t('Certification Documents')}`}
                      required
                      readOnly={false}
                      mode="minimal"
                    />
                  )}
                />
              </Grid>
            ))}

            {typeName === 'tire_certifications' && jobsList.includes('ase_certified')
              ? (
                <Grid item xs={12} style={{ marginTop: 7 }}>
                  {props.action === 'add'
                    ? (
                      <Controller
                        control={control}
                        name="ase_certified"
                        rules={{ required: true }}
                        shouldUnregister
                        render={({ field }) => (
                          <DealerAutocomplete
                            id="ase_certified"
                            variant="standard"
                            multiple
                            disableClearable
                            suggestions={aseCertifications}
                            placeholder={`${getOptionLabel('spp_mechanical_certifications_jobs_tire_certifications', 'ase_certified')} ${t('Certifications')}`}
                            label={`${getOptionLabel('spp_mechanical_certifications_jobs_tire_certifications', 'ase_certified')} ${t('Certifications')}`}
                            values={field.value}
                            disabledOptions={[]}
                            readOnly={false}
                            disabled={!aseCertifications}
                            error={!!errors.ase_certified}
                            disableCloseOnSelect
                            onChange={(newValue: string[]) => field.onChange(newValue)}
                          />
                        )}
                      />
                    )
                    : (
                      <Controller
                        control={control}
                        name="certification"
                        rules={{ required: true }}
                        shouldUnregister
                        render={({ field }) => (
                          <Select
                            id="certification"
                            label={`${getOptionLabel('spp_mechanical_certifications_jobs_tire_certifications', 'ase_certified')} ${t('Certifications')}`}
                            options={aseCertifications}
                            error={errors.certification}
                            readOnly={false}
                            disabled={!aseCertifications || fileOnly}
                            required
                            fullWidth
                          /* eslint-disable-next-line react/jsx-props-no-spreading */
                            {...field}
                          />
                        )}
                      />
                    )}
                </Grid>
              )
              : null}

            {typeName === 'tire_certifications' && jobsList.includes('tia_certified')
              ? (
                <Grid item xs={12} style={{ marginTop: 7 }}>
                  {props.action === 'add'
                    ? (
                      <Controller
                        control={control}
                        shouldUnregister
                        name="tia_certified"
                        rules={{ required: true }}
                        render={({ field }) => (
                          <DealerAutocomplete
                            id="tia_certified"
                            variant="standard"
                            multiple
                            disableClearable
                            suggestions={tiaCertifications}
                            placeholder={`${getOptionLabel('spp_mechanical_certifications_jobs_tire_certifications', 'tia_certified')} ${t('Certifications')}`}
                            label={`${getOptionLabel('spp_mechanical_certifications_jobs_tire_certifications', 'tia_certified')} ${t('Certifications')}`}
                            values={field.value}
                            disabledOptions={[]}
                            readOnly={false}
                            disabled={!tiaCertifications}
                            error={!!errors.tia_certified}
                            disableCloseOnSelect
                            onChange={(newValue: string[]) => field.onChange(newValue)}
                          />
                        )}
                      />
                    ) : (
                      <Controller
                        control={control}
                        shouldUnregister
                        name="certification"
                        rules={{ required: true }}
                        render={({ field }) => (
                          <Select
                            id="certification"
                            label={`${getOptionLabel('spp_mechanical_certifications_jobs_tire_certifications', 'tia_certified')} ${t('Certifications')}`}
                            options={tiaCertifications}
                            error={errors.certification}
                            readOnly={false}
                            disabled={!tiaCertifications || fileOnly}
                            required
                            fullWidth
                          /* eslint-disable-next-line react/jsx-props-no-spreading */
                            {...field}
                          />
                        )}
                      />
                    ) }
                </Grid>
              )
              : null}

            {typeName === 'tire_certifications' && jobsList.includes('tia_certified') && tiaCertsList.map((cert:string) => (
              <Grid item xs={12} style={{ marginTop: 7 }}>
                <Controller
                  control={control}
                  shouldUnregister
                  /* eslint-disable-next-line react/jsx-props-no-spreading */
                  {...register(props.action === 'edit'
                    ? 'file'
                    : `files.tire_certifications.tia_certified.${cert}`, { required: true })
                  }
                  render={(item) => (
                    <FileUpload
                      error={!!errors?.files?.tire_certifications?.tia_certified[cert]}
                      data={item.field.value}
                      onChange={item.field.onChange}
                      onUploading={setUploading}
                      label={`${getOptionLabel('spp_mechanical_certifications_certifications_tia', cert)} ${t('Certification Documents')}`}
                      required
                      readOnly={false}
                      mode="minimal"
                    />
                  )}
                />
              </Grid>
            ))}
          </Grid>
        </ClosableDialogContent>
        <DialogActions>
          <Button
            size="small"
            color="primary"
            style={{ margin: '8px' }}
            disabled={!isDirty || uploading}
            onClick={handleSubmit(save, onError)}
          >
            {props.action === 'edit' ? t('Update') : t('Add')}
          </Button>
          <Button
            size="small"
            color="default"
            style={{ margin: '8px' }}
            disabled={isSubmitting || uploading}
            onClick={() => {
              if (isDirty) setPreventTransition(true);
              else props.onClose();
            }}
          >
            {t('Cancel')}
          </Button>
        </DialogActions>
      </Dialog>
      <PreventTransitionDialog
        open={preventTransition}
        handleCancel={() => { setPreventTransition(false); }}
        handleClose={() => { setPreventTransition(false); props.onClose(); }}
        handleDiscard={() => { setPreventTransition(false); props.onClose(); }}
        message={t('You have unsaved changes...')}
        title={t('Do you want to discard those changes?')}
      />
    </>
  );
}

export {
  MechanicCertificationsDetail,
};
